import { z } from 'zod';
export const WorkspaceKind = z.enum(['product', 'support']);
export const WorkflowStage = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{0,63}$/),
    name: z.string().trim().min(1).max(80),
    category: z.enum(['preparation', 'ready', 'new', 'working', 'waiting_customer', 'waiting_engineering', 'resolved', 'closed']),
    review: z.boolean().default(false),
    archived: z.boolean().default(false),
});
export const WorkflowConfigInput = z.object({
    expectedVersion: z.number().int().min(0),
    enabled: z.boolean(),
    stages: z.array(WorkflowStage).min(1).max(40),
    replacements: z.record(z.string()).default({}),
    airaUrl: z.string().url().startsWith('https://').nullable().optional(),
}).strict();
export const WorkspaceChangeInput = z.object({
    expectedVersion: z.number().int().min(0),
    action: z.enum(['enroll', 'move', 'ready', 'return', 'update']),
    kind: WorkspaceKind.optional(),
    stageId: z.string().optional(),
    ownerId: z.string().nullable().optional(),
    waitingReason: z.enum(['internal_question', 'customer_question', 'internal_hold', 'customer_hold']).nullable().optional(),
    reviewState: z.enum(['needs_work', 'in_review', 'accepted']).optional(),
    nextAction: z.string().max(2000).nullable().optional(),
    followUpAt: z.string().datetime().nullable().optional(),
    expedite: z.boolean().optional(),
    reason: z.string().trim().max(2000).optional(),
}).strict();
export const EscalateInput = z.object({
    expectedVersion: z.number().int().min(0),
    idempotencyKey: z.string().min(8).max(100),
    targetTicketId: z.string().optional(),
    title: z.string().trim().min(1).max(300).optional(),
    summary: z.string().trim().min(1).max(20000),
    type: z.enum(['internal_bug', 'feature_request', 'task']).default('internal_bug'),
    repo: z.string().max(200).optional(),
}).strict();
export const DEFAULT_WORKFLOW_STAGES = {
    product: [
        ['new', 'New'], ['requirements', 'Requirements'], ['design', 'Design'],
        ['acceptance_criteria', 'Acceptance Criteria'], ['estimate', 'Estimate'], ['approval', 'Approval'],
        ['dev_ready', 'Dev Ready'], ['proposal_estimate', 'Proposal Estimate'], ['proposal_signature', 'Proposal Signature'],
    ].map(([id, name]) => ({ id: id, name: name, category: id === 'dev_ready' ? 'ready' : 'preparation',
        review: ['requirements', 'design', 'acceptance_criteria'].includes(id), archived: id.startsWith('proposal_') })),
    support: [
        ['new', 'New', 'new'], ['triage', 'Triage', 'new'], ['working', 'Working', 'working'],
        ['waiting_customer', 'Waiting on Customer', 'waiting_customer'],
        ['waiting_engineering', 'Waiting on Engineering', 'waiting_engineering'],
        ['resolved', 'Resolved', 'resolved'], ['closed', 'Closed', 'closed'],
    ].map(([id, name, category]) => ({ id: id, name: name, category: category, review: false, archived: false })),
};
export function workflowCanonicalStatus(kind, stage) {
    if (kind === 'product')
        return stage.category === 'ready' ? 'approved' : 'backlog';
    if (stage.category === 'new')
        return 'triage';
    if (['resolved', 'closed'].includes(stage.category))
        return 'done';
    return 'in_progress';
}
/**
 * A workspace queue ticket counts toward "Open queue" until its status is done.
 * Shared by the workspace page's Open queue card and the CX metric (cx-v1).
 */
export function workspaceQueueOpen(t) {
    return t.status !== 'done';
}
//# sourceMappingURL=workspace.js.map